import { memo, useEffect, useState } from 'react';
import { useReactFlow } from '@xyflow/react';

import {
  StickyNoteColor,
  STICKY_NOTE_COLOR_OPTIONS,
  STICKY_NOTE_DEFAULT_WIDTH,
  STICKY_NOTE_DEFAULT_HEIGHT,
  DEFAULT_STICKY_NOTE_COLOR,
} from '@/types/node-types';

interface StickyNotePlacementPreviewProps {
  active: boolean;
  color: StickyNoteColor;
}

/**
 * Semi-transparent sticky note that follows the cursor while placement mode
 * is active. Pointer events are disabled so canvas clicks pass through to onPaneClick.
 */
export const StickyNotePlacementPreview = memo(
  ({ active, color }: StickyNotePlacementPreviewProps) => {
    const { getZoom } = useReactFlow();
    const [mouse, setMouse] = useState({ x: 0, y: 0 });
    const [visible, setVisible] = useState(false);
    const [zoom, setZoom] = useState(1);

    const { bgColor, borderColor } =
      STICKY_NOTE_COLOR_OPTIONS[color] ??
      STICKY_NOTE_COLOR_OPTIONS[DEFAULT_STICKY_NOTE_COLOR];

    useEffect(() => {
      if (!active) {
        setVisible(false);
        return;
      }

      const handleMouseMove = (event: MouseEvent) => {
        setMouse({ x: event.clientX, y: event.clientY });
        setZoom(getZoom());
        setVisible(true);
      };

      document.addEventListener('mousemove', handleMouseMove);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }, [active, getZoom]);

    if (!active || !visible) return null;

    const width = STICKY_NOTE_DEFAULT_WIDTH * zoom;
    const height = STICKY_NOTE_DEFAULT_HEIGHT * zoom;

    return (
      <div
        data-testid='sticky_note_placement_preview'
        aria-hidden
        className='pointer-events-none fixed rounded-sm border shadow-md opacity-70'
        style={{
          zIndex: 10,
          width,
          height,
          left: mouse.x - width / 2,
          top: mouse.y - height / 2,
          backgroundColor: bgColor,
          borderColor: borderColor,
        }}
      />
    );
  },
);

StickyNotePlacementPreview.displayName = 'StickyNotePlacementPreview';

/** Screen-space half extents for centering placement on click. */
export function getStickyNotePlacementScreenOffset(zoom: number) {
  return {
    x: (STICKY_NOTE_DEFAULT_WIDTH * zoom) / 2,
    y: (STICKY_NOTE_DEFAULT_HEIGHT * zoom) / 2,
  };
}
